export type ProjectMarginSortKey =
  | "work"
  | "client"
  | "status"
  | "salesBudget"
  | "realProfit"
  | "profitMargin"
  | "links"
  | "actions";

export type ProjectMarginSortDirection = "asc" | "desc";

export type ProjectMarginSortableRow = {
  installationId: number;
  expedienteCode?: string | null;
  workDescription?: string | null;
  clientName?: string | null;
  status?: string | null;
  salesBudgetCents: number | null;
  realProfitCents: number | null;
  profitMarginPct: number | null;
  hasBudget: boolean;
  comparisonReady: boolean;
};

export type ProjectMarginSort = {
  key: ProjectMarginSortKey;
  direction: ProjectMarginSortDirection;
};

const statusLabels: Record<string, string> = {
  in_progress: "Iniciada",
  completed: "Completada",
  cancelled: "Cancelada",
  pending: "Pendiente",
};

function textValue(value: unknown) {
  return String(value ?? "").trim().toLocaleLowerCase("es");
}

function numberValue(value: number | null | undefined) {
  return Number.isFinite(value) ? Number(value) : 0;
}

export function getProjectMarginSortValue(row: ProjectMarginSortableRow, key: ProjectMarginSortKey) {
  switch (key) {
    case "work":
      return textValue(`${row.expedienteCode ?? row.installationId} ${row.workDescription ?? ""}`);
    case "client":
      return textValue(row.clientName);
    case "status":
      return textValue(statusLabels[row.status ?? ""] ?? row.status ?? "Pendiente");
    case "salesBudget":
      return numberValue(row.salesBudgetCents);
    case "realProfit":
      return numberValue(row.realProfitCents);
    case "profitMargin":
      return numberValue(row.profitMarginPct);
    case "links":
      return Number(row.hasBudget) + Number(row.comparisonReady);
    case "actions":
      // Las acciones no tienen un valor propio; usan el expediente como criterio estable.
      return row.installationId;
  }
}

export function compareProjectMarginRows(
  left: ProjectMarginSortableRow,
  right: ProjectMarginSortableRow,
  key: ProjectMarginSortKey,
) {
  const leftValue = getProjectMarginSortValue(left, key);
  const rightValue = getProjectMarginSortValue(right, key);

  if (typeof leftValue === "number" && typeof rightValue === "number") {
    if (leftValue !== rightValue) return leftValue < rightValue ? -1 : 1;
  } else {
    const comparison = String(leftValue).localeCompare(String(rightValue), "es", {
      numeric: true,
      sensitivity: "base",
    });
    if (comparison !== 0) return comparison;
  }

  return left.installationId - right.installationId;
}

export function sortProjectMarginRows<T extends ProjectMarginSortableRow>(
  rows: readonly T[],
  sort: ProjectMarginSort,
) {
  const direction = sort.direction === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => direction * compareProjectMarginRows(left, right, sort.key));
}

export function toggleProjectMarginSort(
  current: ProjectMarginSort | null,
  key: ProjectMarginSortKey,
): ProjectMarginSort {
  if (current?.key === key) {
    return { key, direction: current.direction === "desc" ? "asc" : "desc" };
  }
  return { key, direction: "desc" };
}
